import { useApolloClient } from '@apollo/client';
import { datadogLogs } from '@datadog/browser-logs';
import type { HouseholdModelPortfolioModel } from '@ovComponents/4-module/widgets/householdModelPortfolio/householdModelPortfolio.types';
import { FETCH_HOUSEHOLD_MODEL_PORTFOLIO_DETAILS } from '@ovComponents/4-module/widgets/householdModelPortfolio/queries';
import {
  loadHouseholdAttachedModel,
  stagedHouseholdBaseline,
  stagedHouseholdDraft,
} from '@ovComponents/4-module/workflowCompletion/subSteps/householdModelPortfolioDraft/householdDraftStaging';
import { WorkflowContext } from '@ovComponents/4-module/workflowCompletion/workflowCompletionContext';
import type { HouseholdModelPortfolioSectionSnapshot } from '@ovComponents/4-module/workflowCompletion/workflowTypes';
import { useToast } from 'hephaestus/web';
import { cloneDeep } from 'lodash-es';
import { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  type SelectHouseholdParentModelPortfolioOptions,
  type SelectHouseholdParentModelPortfolioSelection,
  SelectHouseholdParentModelPortfolioVisual,
} from './selectHouseholdParentModelPortfolio.visual';

type FetchHouseholdModelPortfolioDetailsData = {
  fetchModelPortfolio?: {
    modelPortfolio?: HouseholdModelPortfolioModel | null;
  } | null;
};

/**
 * Splits a fetched base model into the snapshot's shape: `details` holds only
 * the Model-tab fields, while the larger arrays/IPS narrative fields are
 * pulled out so they aren't duplicated a second time inside `details` too
 * (they're already duplicated once more in the `modelPortfolioDetails` draft,
 * which is unavoidable since draft and snapshot must be independent copies).
 */
const splitBaseModelForSnapshot = (
  model: HouseholdModelPortfolioModel | undefined,
): HouseholdModelPortfolioSectionSnapshot => {
  if (!model) return {};

  const {
    children,
    assetClassBranches,
    targetAllocations,
    translatedPortfolioDescription,
    translatedInvestmentObjective,
    translatedLiquidity,
    translatedPortfolioDisclaimer,
    ...details
  } = model;

  return {
    details,
    strategy: model.strategy,
    children,
    targetAllocations,
    assetClassLevel: model.assetClassLevel,
    assetClassBranches,
    ips: {
      translatedPortfolioDescription,
      translatedInvestmentObjective,
      translatedLiquidity,
      translatedPortfolioDisclaimer,
    },
  };
};

export type SelectHouseholdParentModelPortfolioProps = {
  options: SelectHouseholdParentModelPortfolioOptions;
  onNext: () => void;
  stepLoading?: boolean;
  clientGroupId?: string;
};

export const SelectHouseholdParentModelPortfolio = ({
  options,
  onNext,
  stepLoading = false,
  clientGroupId,
}: SelectHouseholdParentModelPortfolioProps) => {
  const { workflowData, setWorkflowData } = useContext(WorkflowContext);
  const graphqlClient = useApolloClient();
  const { enqueueToast } = useToast();
  const { t } = useTranslation('workflowCompletions');
  const [fetchingBaseModel, setFetchingBaseModel] = useState(false);
  const householdId =
    workflowData.currentHouseholdClientGroupId ?? clientGroupId;

  /**
   * What the household holds today, kept before anything replaces it.
   *
   * This is the last step that can ask: Initialize repoints the household at
   * the new clone, so from there on the question answers with the proposal.
   * Taken once — coming back to pick a different parent must not re-ask —
   * and a household with nothing attached simply has no baseline, which is
   * the first-attach case rather than a failure.
   *
   * Answers with the household's owning organization, which the same fetch
   * carries and the commit steps scope their mutations to.
   */
  const stageBaseline = async () => {
    if (!householdId) return;
    if (workflowData.householdModelPortfolioBaseline) {
      return workflowData.entityOrganizationId;
    }

    try {
      const { model: attached, householdOrganizationId } =
        await loadHouseholdAttachedModel(graphqlClient, householdId);
      if (!attached) return householdOrganizationId;

      await setWorkflowData((previousData) =>
        previousData.householdModelPortfolioBaseline
          ? previousData
          : {
              ...previousData,
              householdModelPortfolioBaseline:
                stagedHouseholdBaseline(attached),
              entityOrganizationId:
                previousData.entityOrganizationId ??
                householdOrganizationId ??
                undefined,
            },
      );

      return householdOrganizationId;
    } catch (caught) {
      // Worth continuing without: the steps that compare say so themselves
      // rather than stranding the advisor on the model picker.
      datadogLogs.logger.error(
        'Household model portfolio baseline load failed',
        { clientGroupId: householdId },
        caught instanceof Error ? caught : undefined,
      );
    }
  };

  const handleContinue = async ({
    financialProductId,
    tradingProcess,
  }: SelectHouseholdParentModelPortfolioSelection) => {
    setFetchingBaseModel(true);
    try {
      const { data } =
        await graphqlClient.query<FetchHouseholdModelPortfolioDetailsData>({
          query: FETCH_HOUSEHOLD_MODEL_PORTFOLIO_DETAILS,
          variables: { input: { modelPortfolioId: financialProductId } },
          fetchPolicy: 'network-only',
        });
      const baseModel = data?.fetchModelPortfolio?.modelPortfolio ?? undefined;

      // The query can succeed while returning no model (e.g. bad id, deleted,
      // or not visible to the org). Don't seed an empty snapshot/draft and
      // advance — surface the error toast and stay on this step.
      if (!baseModel) {
        throw new Error(
          `No model portfolio returned for id ${financialProductId}`,
        );
      }

      const householdOrganizationId = await stageBaseline();

      const baseModelSnapshot: HouseholdModelPortfolioSectionSnapshot =
        cloneDeep(splitBaseModelForSnapshot(baseModel));

      // Separate clone from the snapshot, and staged the way every branch
      // stages one, so the securities step opens on the model's own holdings.
      const modelPortfolioDetails = stagedHouseholdDraft(baseModel);

      // Merge via the functional updater against the latest context — not a
      // spread of the render-closure `workflowData`, which would clobber any
      // context writes that landed while the fetch was in flight. `setWorkflowData`
      // persists the new context to the server (with retry) and only resolves once
      // that write completes, so no follow-up `updateWorkflowContext()` is needed
      // here — issuing one would re-persist a stale context and drop the selection.
      await setWorkflowData((previousData) => ({
        ...previousData,
        baseModelPortfolioId: financialProductId,
        tradingProcess,
        baseModelSnapshot,
        modelPortfolioDetails,
        currentHouseholdClientGroupId: householdId,
        entityOrganizationId:
          previousData.entityOrganizationId ??
          householdOrganizationId ??
          undefined,
        // If the parent changed, discard any clone minted from the previous
        // parent so Initialize re-mints instead of attaching a stale clone
        // (`entityBoundModelPortfolioId` is keyed to the old base model).
        ...(previousData.baseModelPortfolioId !== financialProductId
          ? { entityBoundModelPortfolioId: undefined }
          : {}),
      }));

      onNext();
    } catch {
      enqueueToast(t('householdInitializeShells.selectParentModelError'), {
        variant: 'error',
      });
    } finally {
      setFetchingBaseModel(false);
    }
  };

  return (
    <SelectHouseholdParentModelPortfolioVisual
      options={options}
      loading={stepLoading || fetchingBaseModel}
      onNext={handleContinue}
      initialSelection={{
        themeId: workflowData.baseModelSnapshot?.details?.theme?.id,
        financialProductId: workflowData.baseModelPortfolioId,
        tradingProcess: workflowData.tradingProcess,
      }}
    />
  );
};
